import { plural } from '../util/formato';
import { Botao } from './base';

export interface Paginado<T> {
  readonly itens: T[];
  readonly total: number;
  readonly pagina: number;
  readonly limite: number;
  readonly totalPaginas: number;
}

/**
 * Recorta uma lista já carregada no mesmo formato que o servidor devolveria.
 *
 * Serve para as telas cujo endpoint entrega a lista inteira — a triagem, por
 * exemplo, precisa de todos os candidatos para separar aptos de pendentes antes
 * de mostrar qualquer coisa. Paginar no servidor ali obrigaria duas consultas e
 * ainda assim não pouparia trabalho, porque a separação é feita sobre o total.
 *
 * `pagina` é corrigida para dentro dos limites: se a lista encolher entre duas
 * recargas, a página guardada no estado pode passar do fim.
 */
export function recortar<T>(itens: readonly T[], pagina: number, limite: number): Paginado<T> {
  const total = itens.length;
  const totalPaginas = Math.max(1, Math.ceil(total / limite));
  const paginaValida = Math.min(Math.max(1, pagina), totalPaginas);
  const inicio = (paginaValida - 1) * limite;
  return {
    itens: itens.slice(inicio, inicio + limite),
    total,
    pagina: paginaValida,
    limite,
    totalPaginas,
  };
}

/**
 * Paginação.
 *
 * Mostra a FAIXA visível ("41 a 80 de 300"), não só o número da página. Numa
 * carteira de 300 devedores, "página 2 de 6" não ajuda a saber onde se está — a
 * faixa, sim.
 */
export function Paginacao({
  dados,
  aoMudarPagina,
  nomeDoItem = ['registro', 'registros'],
}: {
  dados: Paginado<unknown>;
  aoMudarPagina: (pagina: number) => void;
  nomeDoItem?: readonly [string, string];
}) {
  if (dados.total === 0) return null;

  const primeiro = (dados.pagina - 1) * dados.limite + 1;
  const ultimo = Math.min(dados.pagina * dados.limite, dados.total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
      <p className="tabular text-xs text-[color:var(--texto-suave)]">
        {primeiro.toLocaleString('pt-BR')} a {ultimo.toLocaleString('pt-BR')} de{' '}
        {plural(dados.total, nomeDoItem[0], nomeDoItem[1])}
      </p>

      {dados.totalPaginas > 1 && (
        <div className="flex items-center gap-2">
          <Botao
            onClick={() => aoMudarPagina(dados.pagina - 1)}
            desabilitado={dados.pagina <= 1}
            className="px-2.5 py-1 text-xs"
          >
            ← Anterior
          </Botao>
          <span className="tabular px-1 text-xs text-[color:var(--texto-tenue)]">
            {dados.pagina} / {dados.totalPaginas}
          </span>
          <Botao
            onClick={() => aoMudarPagina(dados.pagina + 1)}
            desabilitado={dados.pagina >= dados.totalPaginas}
            className="px-2.5 py-1 text-xs"
          >
            Próxima →
          </Botao>
        </div>
      )}
    </div>
  );
}
